import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ValidateDocument from '@Application/Beneficiaries/Documents/Commands/ValidateDocument';
import validateDocument from '@Application/Beneficiaries/Documents/validateDocument';
import FetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/Queries/FetchWorkflowRun';
import fetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/fetchWorkflowRun';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useCanEditDocument from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useCanEditDocument';
import useDocumentRoute from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentRoute';
import type { DocumentListReturn } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentListReturn';
import type { ReviewStage } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useReviewStage';
import useDocumentViewer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/useDocumentViewer';
import ValidationDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/DocumentActions/ValidateDocumentAction/ValidationDialog';
import editingFieldSignal from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/editingFieldSignal';
import { Check } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

type Props = {
	readonly subject: SubjectAddress;
	/** The stage the screen was opened for, `null` when it was opened on a single document. */
	readonly reviewStage: ReviewStage | null;
	/** Where to go after validating when opened from the documents list, `null` otherwise. */
	readonly listReturn: DocumentListReturn | null;
};

type DialogKind = 'blocked' | 'warning';

/**
 * « Valider le document »: hidden unless the reader can write and the server says the document can be
 * validated, disabled while a draft is modified. Invalid fields block with a dialog, fields left to
 * verify ask for a confirmation, otherwise the validation is sent directly. Opened from a preparation,
 * a document that completes its stage returns to the preparation, which is refreshed otherwise.
 * Opened from the documents list, it returns to the list once validated.
 */
const ValidateDocumentAction: React.FC<Props> = ({ subject, reviewStage, listReturn }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const document = useSelector((state) => state.documentsStore.document);
	const canEdit = useCanEditDocument();
	const editingField = useSignalValue(editingFieldSignal);
	const { goToField, goToAdjacentPending } = useDocumentViewer();
	const { facilityId, documentId } = useDocumentRoute();

	const [dialogKind, setDialogKind] = React.useState<DialogKind | null>(null);
	const [isValidating, setIsValidating] = React.useState(false);

	const invalidLabels = React.useMemo(
		() =>
			document === null
				? []
				: [
						...new Set(
							document
								.orderedFields()
								.filter((field) => field.isRejectedValue)
								.map((field) => field.label),
						),
					],
		[document],
	);

	const unverifiedCount =
		document?.progress.byStatus.find((entry) => entry.status.value === 'unverified')?.count ?? 0;
	const remainingCount = document?.progress.remaining ?? 0;
	const invalidCount = document?.progress.invalid ?? 0;
	const firstInvalidKey = document?.firstInvalidField?.key ?? null;

	const handleClose = React.useCallback(() => {
		setDialogKind(null);
	}, []);

	const validate = React.useCallback(() => {
		setIsValidating(true);

		dispatch(validateDocument(new ValidateDocument(facilityId, subject, documentId)))
			.then(async (action) => {
				setDialogKind(null);

				if (action.type !== 'DOCUMENT_VALIDATED') {
					return;
				}

				if (reviewStage === null) {
					if (listReturn !== null) {
						navigate(listReturn.listPath);
					}

					return;
				}

				const isStageDone = reviewStage.documents.every(
					(candidate) =>
						candidate.documentId === documentId ||
						(candidate.documentId !== null && !candidate.isToReview),
				);

				if (isStageDone) {
					navigate(reviewStage.preparationPath);
					return;
				}

				await dispatch(
					fetchWorkflowRun(new FetchWorkflowRun(facilityId, subject, reviewStage.preparationId)),
				);
			})
			.catch(ignoreHandledError)
			.finally(() => {
				setIsValidating(false);
			});
	}, [dispatch, navigate, facilityId, subject, documentId, reviewStage, listReturn]);

	const handleClick = React.useCallback(() => {
		if (invalidCount > 0) {
			setDialogKind('blocked');
			return;
		}

		if (remainingCount > 0) {
			setDialogKind('warning');
			return;
		}

		validate();
	}, [invalidCount, remainingCount, validate]);

	const handleGoToField = React.useCallback(() => {
		const kind = dialogKind;
		setDialogKind(null);

		if (kind === 'blocked' && firstInvalidKey !== null) {
			goToField(firstInvalidKey);
			return;
		}

		goToAdjacentPending('next');
	}, [dialogKind, firstInvalidKey, goToField, goToAdjacentPending]);

	if (!canEdit || document === null || !document.canValidate) {
		return null;
	}

	return (
		<React.Fragment>
			<Button
				type="button"
				label="Valider le document"
				icon={<Check size={16} aria-hidden="true" />}
				severity="success"
				loading={isValidating}
				disabled={editingField?.isDirty === true}
				onClick={handleClick}
			/>
			<ValidationDialog
				visible={dialogKind !== null}
				kind={dialogKind ?? 'warning'}
				invalidLabels={invalidLabels}
				toVerifyCount={Math.max(remainingCount - unverifiedCount, 0)}
				unverifiedCount={unverifiedCount}
				isValidating={isValidating}
				onGoToField={handleGoToField}
				onConfirm={validate}
				onClose={handleClose}
			/>
		</React.Fragment>
	);
};

export default ValidateDocumentAction;
